/** Rulers in canvas pixels along the top and left of the viewport; dragging from one creates a guide. */
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { Rect } from '../model/commands.ts';

export const RULER = 20;
const STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000];

interface Props {
  width: number;
  height: number;
  scrollLeft: number;
  scrollTop: number;
  ox: number;
  oy: number;
  scale: number;
  selection: Rect | undefined;
  onStartGuide: (axis: 'x' | 'y', e: ReactPointerEvent) => void;
}

function ticks(length: number, scroll: number, origin: number, scale: number): Array<{ pos: number; value: number; major: boolean }> {
  const step = STEPS.find((s) => s * scale >= 60) ?? 2000;
  const minor = step / 5;
  const from = Math.floor((scroll - origin) / scale / minor) * minor;
  const to = (scroll + length - origin) / scale;
  const out: Array<{ pos: number; value: number; major: boolean }> = [];
  for (let v = from; v <= to; v += minor) {
    const value = Math.round(v * 1000) / 1000;
    out.push({ pos: origin + value * scale - scroll, value, major: Math.abs(value / step - Math.round(value / step)) < 1e-6 });
  }
  return out;
}

export function Rulers({ width, height, scrollLeft, scrollTop, ox, oy, scale, selection, onStartGuide }: Props) {
  const tx = ticks(width, scrollLeft, ox, scale);
  const ty = ticks(height, scrollTop, oy, scale);
  return (
    <>
      <div className="ruler-corner" aria-hidden="true" />
      <svg className="ruler ruler-top" height={RULER} onPointerDown={(e) => onStartGuide('x', e)} aria-hidden="true">
        {selection && <rect className="ruler-sel" x={ox + selection.x * scale - scrollLeft} width={selection.width * scale} y={0} height={RULER} />}
        {tx.map((k) => (
          <g key={k.value}>
            <line x1={k.pos} x2={k.pos} y1={k.major ? 6 : 14} y2={RULER} />
            {k.major && (
              <text x={k.pos + 3} y={10}>
                {k.value}
              </text>
            )}
          </g>
        ))}
      </svg>
      <svg className="ruler ruler-left" width={RULER} onPointerDown={(e) => onStartGuide('y', e)} aria-hidden="true">
        {selection && <rect className="ruler-sel" y={oy + selection.y * scale - scrollTop} height={selection.height * scale} x={0} width={RULER} />}
        {ty.map((k) => (
          <g key={k.value}>
            <line y1={k.pos} y2={k.pos} x1={k.major ? 6 : 14} x2={RULER} />
            {k.major && (
              <text x={10} y={k.pos + 3} transform={`rotate(-90 10 ${k.pos + 3})`}>
                {k.value}
              </text>
            )}
          </g>
        ))}
      </svg>
    </>
  );
}
